import {
    Box,
    Typography,
    Paper,
    Divider,
    Chip,
    Stack,
    Avatar,
    Button,
    Grid,
    FormControl,
    Select,
    InputLabel,
    MenuItem,
    Skeleton,
    CircularProgress,
    TextField,
    Alert
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
// 🎯 ADD useQueryClient for invalidation after updates
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import orderService from 'api/orderService';
import { useAdminOrderDetails, useAdminUpdateOrderStatus, useAdminRiderLocation } from 'hooks/useOrder';
import { useAdminAllRiders } from 'hooks/useRiders';
import RoomIcon from '@mui/icons-material/Room';
import DirectionsIcon from '@mui/icons-material/Directions';
import { useSocket } from 'contexts/SocketContext';
import RiderTrackerMap from 'views/common/RiderTrackerMap';

// 🎯 EXPANDED STATUS OPTIONS FOR ADMIN (Full lifecycle)
const statusOptions = ['pending', 'preparing', 'prepared', 'picked', 'delivered', 'canceled'];

// 🔹 Mock hook replaced with useAdminAllRiders from hooks

export default function AdminOrderDetail() {
    const { id } = useParams();
    const queryClient = useQueryClient(); // Used to refetch order details after mutation
    const [selectedStatus, setSelectedStatus] = useState('');
    const [cancelReason, setCancelReason] = useState('');
    const [selectedRiderId, setSelectedRiderId] = useState(''); // 🎯 NEW STATE
    const [errorMessage, setErrorMessage] = useState('');
    const [orderStatus, setOrderStatus] = useState('');

    // ✅ Order Details Query
    const {
        data: orderRes,
        isLoading,
        isError
    } = useAdminOrderDetails(id);

    // ✅ Available Riders Query
    const { data: ridersRes } = useAdminAllRiders({ limit: 100 }); 
    const availableRiders = ridersRes?.data || [];

    const order = orderRes?.data;

    const { socket } = useSocket();
    const hasRider = !!(order?.riderDetails?.id || order?.rider);
    const { data: initialRiderLocRes } = useAdminRiderLocation(hasRider ? id : null);
    const [riderCoords, setRiderCoords] = useState(null);

    // Initial rider location setup
    useEffect(() => {
        if (initialRiderLocRes?.success && initialRiderLocRes.data?.location?.coordinates) {
            const [lng, lat] = initialRiderLocRes.data.location.coordinates;
            setRiderCoords([Number(lat), Number(lng)]);
        }
    }, [initialRiderLocRes]);

    // Live socket rider location tracking
    useEffect(() => {
        if (socket && id && order) {
            const riderId = order.riderDetails?.id || (order.rider && typeof order.rider === 'object' ? order.rider._id || order.rider.id : order.rider);
            
            const handleRiderLocationUpdate = (data) => {
                if (data.orderId === id || data.riderId === riderId) {
                    console.log('Socket rider location update:', data);
                    if (data.coordinates && data.coordinates.length === 2) {
                        const [lng, lat] = data.coordinates;
                        setRiderCoords([Number(lat), Number(lng)]);
                    }
                }
            };

            socket.on('rider_location_updated', handleRiderLocationUpdate);

            return () => {
                socket.off('rider_location_updated', handleRiderLocationUpdate);
            };
        }
    }, [socket, id, order]);

    // sync orderStatus and rider details when data is fetched
    useEffect(() => {
        if (order?.status) {
            setOrderStatus(order.status);
            const currentRiderId = order.riderDetails?.id || (order.rider && typeof order.rider === 'object' ? order.rider._id || order.rider.id : order.rider);
            if (currentRiderId) {
                setSelectedRiderId(currentRiderId);
            }
        }
    }, [order]);

    // ✅ Mutation for Order Status Update
    const updateOrderMutation = useAdminUpdateOrderStatus();

    useEffect(() => {
        if (updateOrderMutation.isSuccess) {
            const res = updateOrderMutation.data;
            if (res.success === false) {
                setErrorMessage(res.message);
            } else {
                setOrderStatus(res.data.status);
                setErrorMessage('');
                setSelectedStatus('');
            }
        }
        if (updateOrderMutation.isError) {
            const err = updateOrderMutation.error;
            setErrorMessage(err?.response?.data?.message || 'Something went wrong during status update');
        }
    }, [updateOrderMutation.isSuccess, updateOrderMutation.isError, updateOrderMutation.data, updateOrderMutation.error]);

    // 🎯 NEW: Mutation for Rider Assignment
    const assignRiderMutation = useMutation({
        mutationFn: async (riderId) => {
            return await orderService.assignRider(id, riderId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['order', id]);
            setErrorMessage('');
            alert(`Rider assigned successfully. Order status updated to 'picked' if applicable.`);
        },
        onError: (err) => {
            setErrorMessage(err?.response?.data?.message || 'Failed to assign rider');
        }
    });

    const handleUpdate = () => {
        const payload = selectedStatus === 'canceled' ? { orderId: id, status: selectedStatus, reason: cancelReason } : { orderId: id, status: selectedStatus };
        updateOrderMutation.mutate(payload);
    };

    // 🎯 NEW: Handle rider assignment
    const handleRiderAssign = () => {
        if (selectedRiderId) {
            assignRiderMutation.mutate(selectedRiderId);
        }
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'pending':
                return 'warning';
            case 'preparing':
                return 'info';
            case 'prepared':
                return 'secondary';
            case 'picked':
                return 'primary'; // Rider status
            case 'delivered':
                return 'success';
            case 'canceled':
                return 'error';
            default:
                return 'default';
        }
    };

    if (isLoading) {
        return (
            <Box p={2}>
                <Skeleton variant="text" width={200} height={40} />
                <Skeleton variant="rectangular" width="100%" height={300} />
            </Box>
        );
    }

    if (isError || !order) {
        return <Typography color="error">Failed to load order details</Typography>;
    }

    // Determine total amount
    const totalAmount = (order.amount || 0) + (order.deliveryFee || 0) + (order.serviceFee || 0);

    // Unpack coordinates from order object
    const getCoordinates = (loc) => {
        if (loc && Array.isArray(loc.coordinates) && loc.coordinates.length === 2) {
            const [lng, lat] = loc.coordinates;
            if (lat && lng) return [Number(lat), Number(lng)];
        }
        return null;
    };

    const pickupCoords = getCoordinates(order?.pickup) || 
                         (Array.isArray(order?.pickupLocation) && order?.pickupLocation.length === 2 
                            ? [order.pickupLocation[1], order.pickupLocation[0]] 
                            : null) ||
                         (order?.vendor?.location?.coordinates && order?.vendor.location.coordinates.length === 2
                            ? [order.vendor.location.coordinates[1], order.vendor.location.coordinates[0]]
                            : null);

    const destCoords = getCoordinates(order?.destination) ||
                       (Array.isArray(order?.deliveryLocation) && order?.deliveryLocation.length === 2
                          ? [order.deliveryLocation[1], order.deliveryLocation[0]]
                          : null);

    return (
        <Box p={2}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
                Admin Order Details - {order.code}
            </Typography>

            <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
                {/* 🎯 RIDER ASSIGNMENT SECTION */}
                <Typography variant="h6" fontWeight={600} gutterBottom>
                    Rider Assignment
                </Typography>
                <Grid container spacing={2} mb={3} alignItems="center">
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth disabled={orderStatus === 'delivered' || orderStatus === 'canceled'}>
                            <InputLabel>Assign Rider</InputLabel>
                            <Select value={selectedRiderId || ''} label="Assign Rider" onChange={(e) => setSelectedRiderId(e.target.value)}>
                                <MenuItem value="">
                                    <em>Unassign/Select New</em>
                                </MenuItem>
                                {availableRiders.map((rider) => (
                                    <MenuItem key={rider._id} value={rider._id}>
                                        {rider.user?.firstName} {rider.user?.lastName} ({rider.riderId || rider._id.slice(-6)})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleRiderAssign}
                            disabled={
                                !selectedRiderId ||
                                assignRiderMutation.isPending ||
                                orderStatus === 'delivered' ||
                                orderStatus === 'canceled'
                            }
                        >
                            {assignRiderMutation.isPending ? <CircularProgress size={24} color="inherit" /> : 'Confirm Assignment'}
                        </Button>
                    </Grid>
                    {(order.riderDetails || (order.rider && typeof order.rider === 'object')) && (
                        <Grid item xs={12}>
                            <Typography variant="body2" mt={1}>
                                <strong>Current Rider:</strong>{' '}
                                <Chip
                                    label={order.riderDetails?.name || order.rider?.name || 'Assigned Rider'}
                                    size="small"
                                    sx={{ ml: 1 }}
                                />
                            </Typography>
                        </Grid>
                    )}
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* STATUS UPDATE SECTION (Admin can override/force status) */}
                <Typography variant="h6" fontWeight={600} gutterBottom>
                    Order Status Management
                </Typography>
                <Grid container spacing={2} mb={2}>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={selectedStatus || order.status}
                                label="Status"
                                onChange={(e) => setSelectedStatus(e.target.value)}
                            >
                                {statusOptions.map((status) => (
                                    <MenuItem key={status} value={status.toLowerCase()}>
                                        {status.toUpperCase()}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {selectedStatus === 'canceled' && (
                        <Grid item xs={12}>
                            <TextField
                                label="Cancel Reason"
                                fullWidth
                                multiline
                                rows={3}
                                value={cancelReason}
                                onChange={(e) => setCancelReason(e.target.value)}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            color="error"
                            disabled={
                                !selectedStatus ||
                                selectedStatus === order.status ||
                                (selectedStatus === 'canceled' && !cancelReason) ||
                                updateOrderMutation.isPending
                            }
                            onClick={handleUpdate}
                        >
                            {updateOrderMutation.isPending ? <CircularProgress size={24} color="inherit" /> : 'Force Status Update'}
                        </Button>
                    </Grid>
                </Grid>

                {/* ... Error Message, Divider, Status Display ... */}
                {errorMessage && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {errorMessage}
                    </Alert>
                )}
                <Stack direction="row" spacing={1} alignItems="center" mb={3}>
                    <Typography variant="body2" color="text.secondary">
                        Current Status:
                    </Typography>
                    <Chip
                        label={orderStatus || order.status}
                        color={getStatusColor(orderStatus || order.status)}
                        size="small"
                        sx={{ fontWeight: 700 }}
                    />
                </Stack>

                <Grid container spacing={4}>
                    {/* LEFT COLUMN: Logistics & Items */}
                    <Grid item xs={12} md={7}>
                        {/* 📍 Logistics Details */}
                        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                            Logistics Details
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#fafafa' }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Stack direction="row" spacing={1} mb={1}>
                                        <RoomIcon color="primary" fontSize="small" />
                                        <Typography variant="body2" fontWeight={700}>
                                            Pickup Location
                                        </Typography>
                                    </Stack>
                                    <Typography variant="caption" sx={{ pl: 3.5, display: 'block' }}>
                                        {order.pickup?.street || order.pickupAddress || 'N/A'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Stack direction="row" spacing={1} mb={1}>
                                        <DirectionsIcon color="success" fontSize="small" />
                                        <Typography variant="body2" fontWeight={700}>
                                            Destination Address
                                        </Typography>
                                    </Stack>
                                    <Typography variant="caption" sx={{ pl: 3.5, display: 'block' }}>
                                        {order.destination?.street || order.deliveryAddress || 'N/A'}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Paper>

                        {/* 🗺️ Live Rider Tracking Map */}
                        {(pickupCoords || destCoords) && (
                            <Box mb={3}>
                                <RiderTrackerMap
                                    pickup={pickupCoords}
                                    destination={destCoords}
                                    rider={riderCoords}
                                    height={350}
                                />
                            </Box>
                        )}

                        {/* 🛒 Items or 📦 Package Details */}
                        {order.orderType === 'products' ? (
                            <>
                                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                                    Ordered Items
                                </Typography>
                                <Stack spacing={1} mb={3}>
                                    {order.products?.map((item) => (
                                        <Box
                                            key={item._id}
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                p: 1,
                                                borderBottom: '1px solid #f0f0f0'
                                            }}
                                        >
                                            <Typography variant="body2">
                                                {item.name} x {item.quantity}
                                            </Typography>
                                            <Typography variant="body2" fontWeight={700}>
                                                ₦{(item.price * item.quantity).toLocaleString()}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            </>
                        ) : (
                            <>
                                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                                    Package Specifications
                                </Typography>
                                <Paper variant="outlined" sx={{ p: 2, mb: 3, borderLeft: '4px solid #ed6c02' }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={3}>
                                            <Typography variant="caption" color="text.secondary">
                                                TYPE
                                            </Typography>
                                            <Typography variant="body2" fontWeight={700} sx={{ textTransform: 'uppercase' }}>
                                                {order.package?.packageType || 'N/A'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={3}>
                                            <Typography variant="caption" color="text.secondary">
                                                WEIGHT
                                            </Typography>
                                            <Typography variant="body2" fontWeight={700}>
                                                {order.package?.weight || 'N/A'} kg
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={3}>
                                            <Typography variant="caption" color="text.secondary">
                                                SIZE
                                            </Typography>
                                            <Typography variant="body2" fontWeight={700} sx={{ textTransform: 'uppercase' }}>
                                                {order.package?.size || 'Standard'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={3}>
                                            <Typography variant="caption" color="text.secondary">
                                                FRAGILE
                                            </Typography>
                                            <Chip
                                                label={order.package?.isFragile ? 'YES' : 'NO'}
                                                size="small"
                                                color={order.package?.isFragile ? 'error' : 'default'}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="caption" color="text.secondary">
                                                DESCRIPTION
                                            </Typography>
                                            <Typography variant="body2">
                                                {order.package?.description || 'No description provided'}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </>
                        )}

                        {/* ✅ Proof of Delivery Section */}
                        {order.deliveryDetails && (
                            <>
                                <Typography variant="subtitle1" fontWeight={700} gutterBottom color="success.main">
                                    Proof of Delivery
                                </Typography>
                                <Paper variant="outlined" sx={{ p: 2, border: '1px solid #4caf50', bgcolor: '#f1f8e9' }}>
                                    <Grid container spacing={2}>
                                        {order.deliveryDetails.deliveryImage && (
                                            <Grid item xs={6}>
                                                <Typography variant="caption" display="block" mb={1}>
                                                    Photo Evidence
                                                </Typography>
                                                <Box
                                                    component="img"
                                                    src={order.deliveryDetails.deliveryImage}
                                                    sx={{
                                                        width: '100%',
                                                        borderRadius: 1,
                                                        maxHeight: 200,
                                                        objectFit: 'cover'
                                                    }}
                                                />
                                            </Grid>
                                        )}
                                        {order.deliveryDetails.customerSignature && (
                                            <Grid item xs={6}>
                                                <Typography variant="caption" display="block" mb={1}>
                                                    Customer Signature
                                                </Typography>
                                                <Box
                                                    component="img"
                                                    src={order.deliveryDetails.customerSignature}
                                                    sx={{
                                                        width: '100%',
                                                        borderRadius: 1,
                                                        bgcolor: '#fff'
                                                    }}
                                                />
                                            </Grid>
                                        )}
                                        {order.deliveryDetails.deliveryNotes && (
                                            <Grid item xs={12}>
                                                <Typography variant="caption">Delivery Notes</Typography>
                                                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                                    "{order.deliveryDetails.deliveryNotes}"
                                                </Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Paper>
                            </>
                        )}
                    </Grid>

                    {/* RIGHT COLUMN: Customer & Financials */}
                    <Grid item xs={12} md={5}>
                        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                            Customer Information
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar>{order.user?.firstName?.[0]}</Avatar>
                                <Box>
                                    <Typography variant="body2" fontWeight={700}>
                                        {order.user?.firstName} {order.user?.lastName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {order.user?.phoneNumber}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>

                        {(order.riderDetails || (order.rider && typeof order.rider === 'object')) && (
                            <>
                                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                                    Assigned Rider Information
                                </Typography>
                                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Avatar sx={{ bgcolor: 'info.main' }}>
                                            {(order.riderDetails?.name || order.rider?.name || 'R')[0]}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="body2" fontWeight={700}>
                                                {order.riderDetails?.name || order.rider?.name || 'Unnamed Rider'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Phone: {order.riderDetails?.phoneNumber || order.riderDetails?.phone || order.rider?.phoneNumber || 'N/A'}
                                            </Typography>
                                            {(order.riderDetails?.email || order.rider?.email) && (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Email: {order.riderDetails?.email || order.rider?.email}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Stack>
                                </Paper>
                            </>
                        )}

                        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                            Financial Summary
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fffde7' }}>
                            <Stack spacing={1}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2">Subtotal</Typography>
                                    <Typography variant="body2">₦{order.amount?.toLocaleString()}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2">Delivery Fee</Typography>
                                    <Typography variant="body2">₦{order.deliveryFee?.toLocaleString()}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2">Service Fee</Typography>
                                    <Typography variant="body2">₦{(order.serviceFee || 0).toLocaleString()}</Typography>
                                </Box>
                                <Divider sx={{ my: 1 }} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="subtitle1" fontWeight={700}>
                                        Total Amount
                                    </Typography>
                                    <Typography variant="subtitle1" fontWeight={700} color="primary">
                                        ₦{totalAmount.toLocaleString()}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>

                        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 3 }}>
                            Settlement Details
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: '#e3f2fd', border: '1px solid #2196f3' }}>
                            <Stack spacing={1}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" fontWeight={700} color="primary">
                                        Platform Revenue
                                    </Typography>
                                    <Typography variant="body2" fontWeight={700} color="primary">
                                        ₦{((order.amount * (order.commission || 0)) / 100 + (order.serviceFee || 0)).toLocaleString()}
                                    </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', pl: 1 }}>
                                    (Commission: ₦{((order.amount * (order.commission || 0)) / 100).toLocaleString()} + Service Fee: ₦
                                    {(order.serviceFee || 0).toLocaleString()})
                                </Typography>

                                <Divider sx={{ my: 1 }} />

                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2">Vendor Share (Net)</Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                        ₦{(order.amount - (order.amount * (order.commission || 0)) / 100).toLocaleString()}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2">Rider Share (Gross)</Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                        ₦{order.deliveryFee?.toLocaleString()}
                                    </Typography>
                                </Box>

                                <Box mt={1} p={1} sx={{ bgcolor: 'rgba(33, 150, 243, 0.05)', borderRadius: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        * Settlements are processed automatically upon order completion.
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>
                    </Grid>
                </Grid>
            </Paper>
        </Box>
    );
}
